import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { nameToSlug } from "../../utils/categoriesMap";
import "./ListingDetails.scss";

import backIcon from "./img/back.png";
//import viewIcon from "./img/view.png";
import sendIcon from "./img/send.png";
import addIcon from "./img/add.png";
import shareIcon from "./img/share.png";
import arrowIcon from "./img/arrow.png";

import callIcon from "../CreateListing/img/call.png";

import categoryFieldsConfig from "./categoryFieldsConfig";
import { getCurrentUserId, getAccessToken } from "../../awsAuth";
import ListingDetailsActions from "./ListingDetailsActions";
import BoostSheet from "./BoostSheet";
import ActionBar from "../common/ActionBar";

import CommentsModal from "../Comments/CommentsModal";
import ShareModal from "../Shared/ShareModal";
import SaveSheetModal from "../Saved/SaveSheetModal";

import SimilarListings from "./SimilarListings";
import AuthorListingsRow from "./AuthorListingsRow";
import AdSlot from "../Ads/AdSlot";

import PhotoDetails from "../PhotoDetails/PhotoDetails";
import { Helmet } from "react-helmet-async";

import EntityNotFound from "../common/EntityNotFound";
import Alerts from "../Alerts/Alerts";
import { useToast } from "../Alerts/Toast";
import {
  trackDwellListing,
  trackLikeListing,
  trackSaveListing,
  trackMessageUser,
  trackOpenProfile,
} from "../../utils/trackActivity";

const getAuthHeaders = async () => {
  const token = await getAccessToken();

  return token
    ? {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }
    : {
        "Content-Type": "application/json",
      };
};

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

  const posterThemes = [
  "blue",
  "violet",
  "graphite",
  "green",
  "bronze",
  "wine",
  "ocean",
  "sand",
];

const getStablePosterTheme = (item, title) => {
  const source = String(
    item?.listingId || item?.id || item?._id || title || "listing-details"
  );

  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  return posterThemes[Math.abs(hash) % posterThemes.length];
};

const getPosterTitleSizeClass = (title) => {
  const len = String(title || "").trim().length;

  if (len <= 18) return "is-short";
  if (len <= 42) return "is-medium";
  return "is-long";
};

const getPureCategory = (category) =>
  category?.replace(/^[^\wА-Яа-яІіЇїЄє]+/, "").trim() || "";

const prettifyKey = (key) => {
  if (!key) return "";
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatAttrValue = (val) => {
  if (Array.isArray(val)) return val.join(", ");
  if (typeof val === "boolean") return val ? "Так" : "Ні";
  if (val === null || val === undefined) return "";
  return String(val);
};

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const now = new Date();
  const created = new Date(Number(timestamp));
  if (Number.isNaN(created.getTime())) return "";

  const diff = (now - created) / 1000;
  if (diff < 60) return "щойно";
  if (diff < 3600) return Math.floor(diff / 60) + " хв тому";
  if (diff < 86400) return Math.floor(diff / 3600) + " год тому";

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    created.getDate() === yesterday.getDate() &&
    created.getMonth() === yesterday.getMonth() &&
    created.getFullYear() === yesterday.getFullYear()
  ) {
    return "вчора";
  }
  return created.toLocaleDateString("uk-UA", { day: "numeric", month: "long" });
};

const normalizePhone = (raw) => {
  if (!raw) return { display: "", href: "" };
  const digits = String(raw).replace(/\D/g, "");
  let normalized = digits;
  if (digits.length === 10 && digits.startsWith("0")) normalized = "38" + digits;
  if (digits.length === 12 && !digits.startsWith("38")) normalized = "38" + digits;
  if (normalized.length === 11 && normalized.startsWith("8")) normalized = "3" + normalized;
  const d = normalized.replace(/^38/, "");
  const display =
    d.length >= 10
      ? `+38 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`
      : `+${normalized}`;
  const href = normalized ? `tel:+${normalized}` : "";
  return { display, href };
};

function useScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const body = document.body;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollbarW = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.paddingRight = prev.paddingRight;
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

function extractKeywords(text, max = 5) {
  if (!text) return [];
  const STOP = new Set([
    "і","й","та","але","або","що","це","цей","ця","це","ті","та","для","без","про","над","під","між","як",
    "у","в","на","до","по","з","із","від","за","не","ні","тощо","ж","би","де","так","ще","вже",
    "я","ти","він","вона","ми","ви","вони","його","її","їх","мене","мені","тебе",
    "и","но","а","в","на","по","с","со","к","до","из","у","о","об","же","ли","не","ни","это",
    "the","a","an","of","for","to","in","on","with","and","or","but","is","are","be","was","were","this","that"
  ]);
  const tokens = String(text).toLowerCase()
    .replace(/[^\p{L}\p{N}\s#@]/gu, " ")
    .split(/\s+/)
    .filter(t => t && t.length >= 3 && !STOP.has(t))
    .slice(0, 50);
  const freq = {};
  for (const t of tokens) freq[t] = (freq[t] || 0) + 1;
  return Object.entries(freq).sort((a,b) => b[1]-a[1]).slice(0, max).map(([t]) => t);
}

const ListingDetails = ({ listingId, isModal = false, onClose, initialListing = null, initialThumb = null, onDeleted }) => {
  const params = useParams();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const dwellStartRef = useRef(null);
  const dwellListingIdRef = useRef(null);
  const dwellCategoryRef = useRef(null);
  const dwellSentRef = useRef(false);

  const [listing, setListing] = useState(initialListing);
  const [isLoading, setIsLoading] = useState(!initialListing);

  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [mounted, setMounted] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const toast = useToast();
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const EXIT_MS = 220;

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  const requestClose = useCallback(() => {
    if (isLeaving) return;
    setIsLeaving(true);
    const t = setTimeout(() => {
      onClose?.();
      setIsLeaving(false);
    }, EXIT_MS);
    return () => clearTimeout(t);
  }, [isLeaving, onClose]);

  const effectiveId = listingId || params.id;

  const [index, setIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const startX = useRef(null);
  const dragging = useRef(false);

  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const [likesCount, setLikesCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savesCount, setSavesCount] = useState(0);
  const [author, setAuthor] = useState({
    username: "",
    avatar: DEFAULT_AVATAR,
    id: null,
  });
  const [isFollowing, setIsFollowing] = useState(false);
  const [isOwn, setIsOwn] = useState(false);

  const [authorPhone, setAuthorPhone] = useState("");

  const [messageText, setMessageText] = useState("");

    const [bottomOffset, setBottomOffset] = useState(0);

  const photos = useMemo(() => {
    if (listing) {
      if (Array.isArray(listing.photos) && listing.photos.length > 0) {
        return listing.photos;
      }

      if (listing.mainPhoto) {
        return [listing.mainPhoto];
      }
    }

    return initialThumb ? [initialThumb] : [];
  }, [listing, initialThumb]);

  const posterTitle = String(listing?.title || "Оголошення").trim();

  const posterTheme = useMemo(() => {
    return getStablePosterTheme(listing, posterTitle);
  }, [listing, posterTitle]);

  const posterTitleSizeClass = useMemo(() => {
    return getPosterTitleSizeClass(posterTitle);
  }, [posterTitle]);

  useEffect(() => {

    try {
      const mStandalone = window.matchMedia && (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.matchMedia("(display-mode: fullscreen)").matches
      );
      const isStandalone = mStandalone || (window.navigator && window.navigator.standalone);
      const ua = navigator.userAgent || "";
      const isIOS = /iP(hone|od|ad)/.test(ua);
      const isMobileSafari =
        isIOS &&
        /Safari/.test(ua) &&
        !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);

      if (!isStandalone && isMobileSafari) {
        setBottomOffset(24);
      } else {
        setBottomOffset(0);
      }
    } catch {
      setBottomOffset(0);
    }
  }, []);

  useEffect(() => {
    if (!effectiveId) return;

    let mountedFlag = true;
    const controller = new AbortController();

    const fetchListing = async () => {
      try {
        if (!initialListing) setIsLoading(true);

        setNotFound(false);
        setLoadError(false);

        const res = await axios.get(`/api/listings/${effectiveId}`, { signal: controller.signal });
        if (!mountedFlag) return;

        setListing(res.data);
        const favs = Number(res.data?.favoritesCount || 0);
        setSavesCount(Number.isFinite(favs) ? favs : 0);
      } catch (err) {
        if (!mountedFlag) return;

        if (err?.name === "CanceledError" || err?.name === "AbortError") return;

        const status = err?.response?.status;

        if (status === 404 || status === 410) {
          setListing(null);
          setNotFound(true);
          setLoadError(false);
          return;
        }

        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.error("Failed to load listing", err);
        }

        setLoadError(true);
      } finally {
        if (mountedFlag) setIsLoading(false);
      }
    };

    fetchListing();
    return () => {
      mountedFlag = false;
      controller.abort();
    };
  }, [effectiveId, initialListing]);

  useEffect(() => {
    const postUniqueView = async () => {
      if (!listing?.listingId) return;
      try {
        const userId = getCurrentUserId();
        if (!userId) return;

        const headers = await getAuthHeaders();

        const res = await axios.post(
          `/api/listings/${listing.listingId}/view-unique`,
          { createdAt: listing.createdAt },
          { headers }
        );

        if (res.data?.incremented === 1) {
          setListing((prev) => ({ ...prev, viewsCount: (prev?.viewsCount || 0) + 1 }));
        }
      } catch (e) {
        console.warn("⚠️ Не вдалося зафіксувати унікальний перегляд:", e?.message || e);
      }
    };
    postUniqueView();
  }, [listing?.listingId, listing?.createdAt]);

  useEffect(() => {
  if (!listing?.listingId) return;

  dwellStartRef.current = Date.now();
  dwellListingIdRef.current = String(listing.listingId);
  dwellCategoryRef.current = listing?.category || null;
  dwellSentRef.current = false;

  return () => {
    if (dwellSentRef.current) return;

    const startedAt = dwellStartRef.current;
    const listingIdToSend = dwellListingIdRef.current;

    if (!startedAt || !listingIdToSend) return;

    const durationMs = Date.now() - startedAt;

    if (durationMs < 7000) return;

    dwellSentRef.current = true;

    trackDwellListing({
      listingId: listingIdToSend,
      category: dwellCategoryRef.current,
      durationMs,
    });
  };
}, [listing?.listingId]);

  const [commentCount, setCommentCount] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareText, setShareText] = useState("");
  const [shareCount, setShareCount] = useState(0);
  const lastShareFetchRef = useRef(0);

  const fetchCommentCount = useCallback(async () => {
    if (!listing?.listingId) return;
    try {
      const { data } = await axios.get(
        `/api/comments/stats/${listing.listingId}`,
        { params: { _ts: Date.now() } }
      );
      setCommentCount(Number(data?.count || 0));
    } catch {
    }
  }, [listing?.listingId]);

  const fetchShareCount = useCallback(async () => {
    if (!listing?.listingId) return;
    const startedAt = Date.now();
    lastShareFetchRef.current = startedAt;
    try {
      const { data } = await axios.get(
        `/api/shares/count/${listing.listingId}`,
        { params: { _ts: Date.now() } }
      );
      if (lastShareFetchRef.current === startedAt) {
        const n = Number(data?.count);
        setShareCount(Number.isFinite(n) ? n : 0);
      }
    } catch {
    }
  }, [listing?.listingId]);

  const registerShare = useCallback(async () => {
    if (!listing?.listingId) return;
    try {
      await axios.post(`/api/shares`, {
        postId: listing.listingId,
        type: "listing",
      });
    } catch {
    } finally {
      fetchShareCount();
    }
  }, [listing?.listingId, fetchShareCount]);

  useEffect(() => {
    const initialComments = Number(listing?.commentsCount || 0);
    if (Number.isFinite(initialComments)) setCommentCount(initialComments);
    const initialShares = Number(listing?.sharesCount || 0);
    if (Number.isFinite(initialShares)) setShareCount(initialShares);

    fetchCommentCount();
    fetchShareCount();
  }, [listing?.commentsCount, listing?.sharesCount, fetchCommentCount, fetchShareCount]);

  const openComments = () => setCommentsOpen(true);
  const openShare = (e) => {
    e?.stopPropagation?.();
    if (!listing?.listingId) return;
    const url = `${window.location.origin}/listing/${listing.listingId}`;
    const txt = listing?.title || (listing?.description ? String(listing.description).slice(0, 140) : "");
    setShareUrl(url);
    setShareText(txt || "");
    setShareOpen(true);
  };

  const systemShare = async (e) => {
  e?.stopPropagation?.();

  if (!listing?.listingId) return;

  const url = `${window.location.origin}/listing/${listing.listingId}`;
  const title = listing?.title || "Cvanto";
  const text =
    listing?.description
      ? String(listing.description).replace(/\s+/g, " ").trim().slice(0, 160)
      : title;

  try {
    if (navigator?.share) {
      await navigator.share({ title, text, url });
      registerShare?.();
      return;
    }
  } catch (err) {
    return;
  }

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Посилання скопійовано", 2200);
    registerShare?.();
  } catch {
    toast.error("Не вдалося поділитися або скопіювати посилання", 3200);
  }
};

  useEffect(() => {
    const syncAll = async () => {
      if (!listing?.listingId) return;
      const userId = getCurrentUserId();

      // автор
      try {
        if (listing.userId) {
          const u = await axios.get(`/api/account/${listing.userId}`);
          setAuthor({
            username: u.data?.username || "Користувач",
            avatar: u.data?.avatar || DEFAULT_AVATAR,
            id: u.data?.id || listing.userId,
          });
          setAuthorPhone(
            u.data?.phone ||
              u.data?.phoneNumber ||
              u.data?.phone_number ||
              ""
          );
          setIsOwn(userId && userId === (u.data?.id || listing.userId));
        }
      } catch (e) {
        console.warn("⚠️ Автор не підтягнувся:", e?.message || e);
        setAuthor({
          username: listing.username || "Користувач",
          avatar: listing.avatar || DEFAULT_AVATAR,
          id: listing.userId || null,
        });
        setIsOwn(userId && userId === listing.userId);
        setAuthorPhone("");
      }

      // лайки
      try {
        const headers = await getAuthHeaders();
        const r = await axios.get(`/api/likes/${listing.listingId}`, { headers });
        const count = Number(r.data?.count || 0);
        setLikesCount(Number.isFinite(count) ? count : 0);
        if (userId && Array.isArray(r.data?.users)) {
          setIsLiked(r.data.users.includes(userId));
        } else {
          setIsLiked(false);
        }
      } catch (e) {
        console.warn("⚠️ Лайки:", e?.message || e);
      }

      // збережені
      try {
        if (userId) {
          const savedHeaders = await getAuthHeaders();
          const s = await axios.get(`/api/saved/${userId}`, { headers: savedHeaders });
          const ids = Array.isArray(s.data) ? s.data.map((it) => it.postId) : [];
          setIsSaved(ids.includes(listing.listingId));
        } else {
          setIsSaved(false);
        }
      } catch (e) {
        console.warn("⚠️ Збережені:", e?.message || e);
      }

      // кількість збережень
      try {
        const c = await axios.get(`/api/saved/count/${listing.listingId}`);
        const cnt = Number(c.data?.count);
        if (Number.isFinite(cnt)) setSavesCount(cnt);
      } catch {}

      // статус підписки
      try {
        if (userId && listing.userId && userId !== listing.userId) {
          const f = await axios.get(`/api/follow/status`, {
            params: { followerId: userId, followeeId: listing.userId },
          });
          if (typeof f.data?.isFollowing === "boolean") setIsFollowing(f.data.isFollowing);
        } else {
          setIsFollowing(false);
        }
      } catch (e) {
        console.warn("⚠️ Підписка:", e?.message || e);
      }
    };

    syncAll();
  }, [listing?.listingId, listing?.userId]);

  // повідомлення
  const sendMessage = async () => {
    const me = getCurrentUserId();
    if (!me) {
      toast.info("Спочатку увійдіть у свій акаунт", 2600);
      return;
    }
    if (!messageText.trim()) return;

    try {
      const headers = await getAuthHeaders();

      await axios.post(
      `/api/messages/chat/send`,
      {
        to: author.id,
        text: `${messageText}\n\n📌 Оголошення: ${listing?.title || ""} (${window.location.href})`,
      },
      { headers }
    );

    trackMessageUser({
      targetUserId: author.id,
    });

    setMessageText("");
    toast.success("Повідомлення надіслано", 2200);
    } catch (err) {
      console.error("sendMessage error:", err);
      alert("Не вдалося надіслати повідомлення");
    }
  };

  useEffect(() => {
    setIndex(0);
    setIsZoomed(false);
  }, [photos.length]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const next = useCallback(() => setIndex((i) => clamp(i + 1, 0, photos.length - 1)), [photos.length]);
  const prev = useCallback(() => setIndex((i) => clamp(i - 1, 0, photos.length - 1)), [photos.length]);

  // свайп
  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    dragging.current = true;
  };
  const onTouchMove = (e) => {
    if (!dragging.current || startX.current == null) return;
    const dx = e.touches[0].clientX - startX.current;
    if (Math.abs(dx) > 50) {
      dx < 0 ? next() : prev();
      dragging.current = false;
      startX.current = null;
    }
  };
  const onTouchEnd = () => {
    dragging.current = false;
    startX.current = null;
  };
  const onMouseDown = (e) => {
    startX.current = e.clientX;
    dragging.current = true;
  };
  const onMouseMove = (e) => {
    if (!dragging.current || startX.current == null) return;
    const dx = e.clientX - startX.current;
    if (Math.abs(dx) > 60) {
      dx < 0 ? next() : prev();
      dragging.current = false;
      startX.current = null;
    }
  };
  const onMouseUp = () => {
    dragging.current = false;
    startX.current = null;
  };
  const onMouseLeave = onMouseUp;

  const toggleZoom = () => setIsZoomed((z) => !z);

  const openPhotoViewer = (i) => {
    setPhotoIndex(typeof i === "number" ? i : index);
    setPhotoOpen(true);
  };

  // лайк
  const toggleLike = async () => {
  if (!listing?.listingId) return;

  const userId = getCurrentUserId();

  if (!userId) {
    toast.info("Спочатку увійдіть у свій акаунт", 2600);
    return;
  }

  const postId = listing.listingId;
  const previousLiked = isLiked;
  const previousCount = likesCount;

  // Миттєво оновлюємо інтерфейс
  setIsLiked(!previousLiked);

  setLikesCount((current) => {
    const count = Number(current || 0);

    return previousLiked
      ? Math.max(0, count - 1)
      : count + 1;
  });

  try {
    const headers = await getAuthHeaders();

    if (previousLiked) {
      await axios.delete("/api/likes", {
        headers,
        data: { postId },
      });
    } else {
      await axios.post(
        "/api/likes",
        { postId },
        { headers }
      );

      trackLikeListing({
        listingId: postId,
        category: listing?.category || null,
      });
    }
  } catch (err) {
    console.error("toggleLike error:", err);

    // Якщо сервер повернув помилку — повертаємо попередній стан
    setIsLiked(previousLiked);
    setLikesCount(previousCount);

    toast.error("Не вдалося оновити лайк", 3200);
  }
};

  const quickSaveToAllIfNeeded = useCallback(async () => {
    if (!listing?.listingId) return;
    if (isSaved) return;
    const userId = getCurrentUserId();
    if (!userId) {
      toast.info("Спочатку увійдіть у свій акаунт", 2600);
      return;
    }
    const postId = listing.listingId;
    setIsSaved(true);
    setSavesCount((c) => (c || 0) + 1);
    setListing((prev) => ({ ...prev, favoritesCount: (prev?.favoritesCount || 0) + 1 }));
    try {
      const headers = await getAuthHeaders();

        await axios.post(
      `/api/saved`,
      {
        userId,
        postId,
        type: "listing",
        collectionId: "all",
      },
      { headers }
    );

    trackSaveListing({
      listingId: postId,
      category: listing?.category || null,
    });
    } catch (err) {
      console.error("quickSaveToAllIfNeeded:", err);
      setIsSaved(false);
      setSavesCount((c) => Math.max(0, (c || 1) - 1));
      setListing((prev) => ({ ...prev, favoritesCount: Math.max(0, (prev?.favoritesCount || 1) - 1) }));
    }
  }, [listing?.listingId, isSaved]);

  const [saveSheetOpen, setSaveSheetOpen] = useState(false);
  const [saveSheetThumb, setSaveSheetThumb] = useState(null);

  const openSaveSheet = async (e) => {
    e?.stopPropagation?.();
    if (!listing?.listingId) return;
    const me = getCurrentUserId();
    if (!me) {
      toast.info("Спочатку увійдіть у свій акаунт", 2600);
      return;
    }
    await quickSaveToAllIfNeeded();
    setSaveSheetThumb(photos?.[0] || null);
    setSaveSheetOpen(true);
  };

  // фолов
  const toggleFollow = async () => {
  if (!author?.id) return;

  const me = getCurrentUserId();

  if (!me) {
    toast.info("Спочатку увійдіть у свій акаунт", 2600);
    return;
  }

  if (me === author.id) return;

  try {
    const headers = await getAuthHeaders();

    if (isFollowing) {
      await axios.delete(`/api/follow`, {
        headers,
        data: {
          followerId: me,
          followeeId: author.id,
        },
      });

      setIsFollowing(false);
    } else {
      await axios.post(
        `/api/follow`,
        {
          followerId: me,
          followeeId: author.id,
        },
        { headers }
      );

      setIsFollowing(true);
    }
  } catch (e) {
    console.error("toggleFollow error:", e);
    toast.error("Не вдалося оновити підписку", 3200);
  }
};

  const [actionsOpen, setActionsOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [boostSheetOpen, setBoostSheetOpen] = useState(false);
  const EXIT_MS_LOCAL = 200;

  const openActions = (e) => {
    e?.stopPropagation?.();
    setActionsOpen(true);
    setLeaving(false);
  };
  const closeActions = useCallback(() => {
    setLeaving(true);
    setTimeout(() => {
      setActionsOpen(false);
      setLeaving(false);
    }, EXIT_MS_LOCAL);
  }, []);

  const uid = getCurrentUserId?.();

  const handleArchive = async () => {
  setConfirmArchiveOpen(true);
};

const runConfirmedArchive = async () => {
  setConfirmArchiveOpen(false);
  try {
    if (!listing?.listingId) return;
    const headers = await getAuthHeaders();

await axios.patch(
  `/api/listings/${listing.listingId}/archive`,
  {
    createdAt: listing.createdAt,
    userId: uid,
  },
  { headers }
);
    toast.success("Переміщено в архів", 2200);
    closeActions();
    if (uid) {
  navigate(`/profile/${uid}?refresh=${Date.now()}`);
} else {
  navigate("/profile");
}
  } catch (err) {
    console.error("Архів:", err);
    toast.error("Не вдалося архівувати", 3200);
  }
};

  const handleDelete = async () => {
  setConfirmDeleteOpen(true);
};

const runConfirmedDelete = async () => {
  setConfirmDeleteOpen(false);

  try {
    const userId = uid;
    if (!userId) {
      toast.info("Спочатку увійдіть у свій акаунт", 2600);
      return;
    }

    const headers = await getAuthHeaders();

      await axios.delete(`/api/listings/${listing.listingId}`, {
        headers,
        data: { createdAt: listing.createdAt },
      });

    toast.success("Видалено", 2200);
    closeActions();

    if (typeof onDeleted === "function") {
      onDeleted(listing.listingId);
    } else {
      navigate(-1);
    }
  } catch (err) {
    console.error("Видалення:", err);
    const msg = err?.response?.data?.error || "Не вдалося видалити";
    toast.error(String(msg), 3200);
  }
};

  const handleEdit = () => {
  if (!listing?.listingId) return;

  // Business products/services have their own dedicated edit form
  // (BusinessModuleCreate, opened from the owner's catalog page) with
  // fields the generic listing edit form doesn't have — route there
  // instead of the generic /edit/listing/:id form. Vacancies are
  // deliberately excluded: BusinessModuleCreate doesn't support editing
  // them yet (out of scope, same as the rest of Package #5).
  if (listing.entityType === "business_product") {
    navigate(`/profile/${listing.userId}/products?edit=${encodeURIComponent(listing.listingId)}`);
    closeActions();
    return;
  }
  if (listing.entityType === "business_service") {
    navigate(`/profile/${listing.userId}/services?edit=${encodeURIComponent(listing.listingId)}`);
    closeActions();
    return;
  }

  if (String(listing?.type || "").toLowerCase() === "wanted") {
    navigate(`/edit/search/${encodeURIComponent(String(listing.listingId))}`);
  } else {
    navigate(`/edit/listing/${encodeURIComponent(String(listing.listingId))}`);
  }

  closeActions();
};

  const handleRaise = () => {
    closeActions();
    setBoostSheetOpen(true);
  };

  const sendSignal = async (event, extra = {}) => {
    try {
      await axios.post(`/api/signals`, {
        userId: uid || "guest",
        listingId: String(listing?.listingId || ""),
        authorId: author?.id || null,
        category: listing?.category || null,
        event,
        dwellMs: extra.dwellMs || 0,
        keywords: extra.keywords || null,
      });
    } catch (e) { /* ignore */ }
  };

  const handleNotInteresting = async () => {
    const kws = extractKeywords(`${listing?.title || ""} ${listing?.description || ""}`, 5);
    await sendSignal("not_interesting", { keywords: kws });
    toast.success("Добре — будемо показувати таке рідше", 2200);
    closeActions();
  };

  const handleWriteAuthor = () => {
  if (!author?.id) return;

  navigate(`/messages?to=${encodeURIComponent(author.id)}`);
  closeActions();
  };

  const overlayOpen = commentsOpen || shareOpen || saveSheetOpen || actionsOpen || photoOpen;

  useScrollLock(isModal || overlayOpen);

  function forceUnlockPageScroll() {
  try {
    const body = document.body;

    const top = body.style.top;
    const lockedY = top && top.startsWith("-")
      ? Math.abs(parseInt(top, 10) || 0)
      : window.scrollY || 0;

    body.style.overflow = "";
    body.style.position = "";
    body.style.top = "";
    body.style.width = "";
    body.style.paddingRight = "";

    document.documentElement.style.overflow = "";

    if (lockedY) {
      window.scrollTo(0, lockedY);
    }
  } catch {}
}

const openAuthorProfile = (e) => {
  e.preventDefault();
  e.stopPropagation();

  if (!author?.id) return;

  trackOpenProfile({
    targetUserId: author.id,
  });

  forceUnlockPageScroll();

  if (isModal) {
    onClose?.();
  }

  navigate(`/profile/${author.id}`);
};

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        if (photoOpen) { setPhotoOpen(false); return; }
        if (saveSheetOpen) { setSaveSheetOpen(false); return; }
        if (shareOpen) { setShareOpen(false); return; }
        if (commentsOpen) { setCommentsOpen(false); return; }
        if (actionsOpen) { closeActions(); return; }
        if (isModal) { requestClose(); return; }
      }
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    photoOpen,
    saveSheetOpen,
    shareOpen,
    commentsOpen,
    actionsOpen,
    closeActions,
    isModal,
    requestClose,
    next,
    prev
  ]);

  if (isLoading && !listing && !initialThumb) {
    return (
      <div
        className={`listing-details listing-details--skeleton`}
        style={{ "--ld-bottom-offset": `${bottomOffset}px` }}
      >
        <div className="ld-card">
          <div className="ld-skel ld-skel__media" />
          <div className="ld-skel ld-skel__user" />
          <div className="ld-skel ld-skel__stats" />
          <div className="ld-skel ld-skel__title" />
          <div className="ld-skel ld-skel__text" />
          <div className="ld-skel ld-skel__text" />
          <div className="ld-skel ld-skel__bottom" />
        </div>
      </div>
    );
  }

  if (!listing && !initialThumb) {
    if (notFound) {
      return (
        <EntityNotFound
          title="Оголошення недоступне"
          onBack={() => {
            if (isModal) requestClose();
            else navigate(-1);
          }}
          onHome={() => navigate("/feed")}
        />
      );
    }

    if (loadError) {
      return (
        <EntityNotFound
          title="Не вдалося завантажити"
          text="Схоже, сталася помилка або немає з’єднання. Спробуйте ще раз."
          onBack={() => {
            if (isModal) requestClose();
            else navigate(-1);
          }}
          onHome={() => navigate("/feed")}
        />
      );
    }

    return null;
  }

  // НАДІЙНИЙ ТОРГ (підтримка різних назв полів / attributes)
  const rawNegotiable =
    listing?.isNegotiable ??
    listing?.negotiable ??
    listing?.bargain ??
    listing?.trade ??
    listing?.torh ??
    listing?.attributes?.isNegotiable ??
    listing?.attributes?.negotiable ??
    listing?.attributes?.bargain ??
    listing?.attributes?.trade ??
    listing?.attributes?.torh;

  const showNegotiable =
    rawNegotiable === true ||
    rawNegotiable === "true" ||
    rawNegotiable === 1 ||
    rawNegotiable === "1" ||
    rawNegotiable === "yes" ||
    rawNegotiable === "так";

  // ОБМІН (exchange) – теж “fallback”
  const rawExchange =
    listing?.exchange ??
    listing?.isExchange ??
    listing?.allowExchange ??
    listing?.attributes?.exchange ??
    listing?.attributes?.isExchange ??
    listing?.attributes?.allowExchange;

  const showExchange =
    rawExchange === true ||
    rawExchange === "true" ||
    rawExchange === 1 ||
    rawExchange === "1" ||
    rawExchange === "yes" ||
    rawExchange === "так";

  // СТАН (нове/вживане) – “fallback” + нормалізація
  const rawCondition =
    listing?.condition ??
    listing?.itemCondition ??
    listing?.state ??
    listing?.isNew ??
    listing?.attributes?.condition ??
    listing?.attributes?.itemCondition ??
    listing?.attributes?.state ??
    listing?.attributes?.isNew;

  const normalizeCondition = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).trim().toLowerCase();

    if (v === true || v === 1 || s === "1" || s === "true") return "new"; // якщо isNew=true
    if (s === "new" || s === "нове" || s === "новий" || s === "новая" || s === "новое") return "new";

    if (v === false || v === 0 || s === "0" || s === "false") return ""; // isNew=false без контексту
    if (s === "used" || s === "вживане" || s === "б/у" || s === "бу" || s === "вживаний" || s === "вживаное") return "used";

    return s;
  };

  const conditionKey = normalizeCondition(rawCondition);
  const conditionLabel =
    conditionKey === "new" ? "Нове" :
    conditionKey === "used" ? "Вживане" :
    "";

  const createdHuman = formatTime(listing?.createdAt);

  const dealType = String(listing?.type || "sale").toLowerCase();
  const isExchangeDeal = dealType === "exchange";
  const isFreeDeal = dealType === "free";
  const isSaleDeal = !isExchangeDeal && !isFreeDeal;

  const exchangeForText = (listing?.exchangeFor || "").trim();

    // SEO (title/description/canonical/OG/schema)
  const seoTitleBase = (listing?.title || "Оголошення").trim();
  const seoCategory = getPureCategory(listing?.category);
  const seoCity = (listing?.location || "").trim();
  const seoPrice =
    Number(listing?.price) > 0
      ? `${Number(listing.price).toLocaleString("uk-UA")} ${listing.currency || "₴"}`
      : "";
  const seoTitle = [seoTitleBase, seoCity, seoPrice ? `— ${seoPrice}` : "", "Cvanto"]
    .filter(Boolean)
    .join(" ");

  const seoDescRaw = (listing?.description || "").replace(/\s+/g, " ").trim();
  const seoDesc = (
    seoDescRaw ||
    `${seoTitleBase}${seoCategory ? `. Категорія: ${seoCategory}.` : ""}${seoCity ? ` Місто: ${seoCity}.` : ""}`
  ).slice(0, 160);

  const seoUrl = `https://cvanto.com/listing/${listing?.listingId || effectiveId}`;
  const seoImage =
    (photos && photos[0]) ? photos[0] : (listing?.mainPhoto || DEFAULT_AVATAR);

  // schema.org/Google's structured-data validator requires priceCurrency
  // to be a real ISO 4217 code — a live production data check (SEO Phase 2
  // audit) found 487 of 494 active listings store the literal Ukrainian
  // abbreviation "ГРН" in `currency`, which the previous check (only
  // recognizing "" / "₴" / "UAH") passed straight through as an invalid
  // priceCurrency on nearly the entire catalog. Mirrors
  // cvanto-ssr/lib/listingSeo.js's resolveSchemaCurrency — same bug, same
  // fix, duplicated because this is a separate deployable app.
  const seoCurrencyRaw = String(listing?.currency || "").trim();
  const seoCurrencyLower = seoCurrencyRaw.toLowerCase();
  const seoCurrencyUpper = seoCurrencyRaw.toUpperCase();
  const UAH_CURRENCY_ALIASES = ["", "₴", "uah", "грн", "грн.", "гривня", "гривні"];
  const seoCurrency = UAH_CURRENCY_ALIASES.includes(seoCurrencyLower)
    ? "UAH"
    : /^[A-Z]{3}$/.test(seoCurrencyUpper)
      ? seoCurrencyUpper
      : "UAH";

const phoneRaw = listing?.showPhone ? (listing?.contactPhone || "") : "";

const { display: phoneDisplay, href: phoneHref } = normalizePhone(phoneRaw);

  // Breadcrumb (Phase 3) — mirrors cvanto-ssr/pages/listing/[id].js's real
  // internal link back to the category page, when the category resolves to
  // one of the 18 known real categories (nameToSlug). No fabricated
  // hierarchy for a category string outside that real taxonomy.
  const breadcrumbCategorySlug = seoCategory ? nameToSlug[seoCategory] : null;
  const breadcrumbCategoryUrl = breadcrumbCategorySlug ? `https://cvanto.com/category/${breadcrumbCategorySlug}` : "";
  const breadcrumbItems = [{ "@type": "ListItem", position: 1, name: "Cvanto", item: "https://cvanto.com" }];
  if (breadcrumbCategoryUrl) {
    breadcrumbItems.push({ "@type": "ListItem", position: 2, name: seoCategory, item: breadcrumbCategoryUrl });
  }
  breadcrumbItems.push({
    "@type": "ListItem",
    position: breadcrumbItems.length + 1,
    name: seoTitleBase,
    item: seoUrl,
  });

  return (
    <div
      ref={rootRef}
      className={`listing-details ${isModal ? "listing-details--modal" : "listing-details--page"} ${mounted && !isLeaving ? "ld-enter" : ""} ${isLeaving ? "ld-leave" : ""} ${overlayOpen ? "listing-details--locked" : ""}`}
      style={{ "--ld-bottom-offset": `${bottomOffset}px` }}
    >

            <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={seoUrl} />

        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Cvanto" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={seoImage} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: seoTitleBase,
            description: seoDescRaw || seoDesc,
            image: (photos && photos.length) ? photos : (seoImage ? [seoImage] : []),
            offers: {
              "@type": "Offer",
              url: seoUrl,
              priceCurrency: seoCurrency,
              price: Number(listing?.price) > 0 ? String(listing.price) : undefined,
              availability: "https://schema.org/InStock",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbItems,
          })}
        </script>
      </Helmet>

      {breadcrumbCategoryUrl && (
        <nav className="listing-details__breadcrumb" aria-label="breadcrumb">
          <Link to="/">Cvanto</Link>
          {" › "}
          <Link to={`/category/${breadcrumbCategorySlug}`}>{seoCategory}</Link>
        </nav>
      )}

      <div className="listing-details__media">
      {/* Фото/карусель */}
      <div
        className={`listing-details__photo-container ${isZoomed ? "zoomed" : ""} ld-chunk ld-chunk-1`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <div className="listing-carousel__viewport">
                    {photos.length === 0 ? (
            <div
              className={`listing-carousel__poster listing-carousel__poster--${posterTheme}`}
              aria-label="Немає фото"
            >
              <div className="listing-carousel__poster-glow" />
              <div className={`listing-carousel__poster-title ${posterTitleSizeClass}`}>
                {posterTitle}
              </div>
            </div>
          ) : (
            photos.map((src, i) => (
              <div
                key={i}
                className={`listing-carousel__slide ${i === index ? "active" : ""}`}
                style={{ transform: `translateX(${(i - index) * 100}%)` }}
                aria-hidden={i !== index}
                onClick={() => openPhotoViewer(i)}
              >
                <img
                  src={src}
                  alt={`${listing?.title || "Оголошення"} — фото ${i + 1}`}
                  className="listing-details__photo"
                  draggable={false}
                  loading="lazy"
                  fetchpriority={i === 0 ? "high" : "auto"}
                  onClick={() => openPhotoViewer(i)}
                />
              </div>
            ))
          )}
        </div>

        {isModal ? (
          <button className="listing-details__close" aria-label="Закрити" onClick={requestClose}>
            ✕
          </button>
        ) : (
          <button className="listing-details__back" aria-label="Назад" onClick={() => navigate(-1)}>
            <img src={backIcon} alt="" aria-hidden="true" />
          </button>
        )}

        {/* Actions */}
        <button
          className="listing-details__share"
          aria-label="Поділитися"
          onClick={systemShare}
        >
          <img src={shareIcon} alt="" aria-hidden="true" />
        </button>

        <button className="listing-details__more" aria-label="Дії" onClick={openActions}>⋯</button>

        {photos.length > 1 && index > 0 && (
          <button
            className="listing-carousel__nav left"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Попереднє фото"
          >
            <img
              src={arrowIcon}
              alt=""
              className="listing-carousel__nav-icon listing-carousel__nav-icon--left"
              draggable={false}
            />
          </button>
        )}
        {photos.length > 1 && index < photos.length - 1 && (
          <button
            className="listing-carousel__nav right"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Наступне фото"
          >
            <img
              src={arrowIcon}
              alt=""
              className="listing-carousel__nav-icon listing-carousel__nav-icon--right"
              draggable={false}
            />
          </button>
        )}

        {photos.length > 1 && (
          <div className="listing-carousel__dots" aria-label="Індикатор слайдів">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`dot ${i === index ? "active" : ""}`}
                aria-label={`Перейти до фото ${i + 1}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Мініатюри */}
      {photos.length > 1 && (
        <div className="listing-carousel__thumbs ld-chunk ld-chunk-2">
          {photos.map((src, i) => (
            <button
              key={i}
              className={`thumb ${i === index ? "active" : ""}`}
              onClick={() => {
                setIndex(i);
                setIsZoomed(false);
              }}
              aria-label={`Відкрити фото ${i + 1}`}
            >
              <img src={src} alt={`${listing?.title || "Оголошення"} — фото ${i + 1}`} loading="lazy" />
            </button>
          ))}
        </div>
      )}
      </div>

      {/* User-bar */}
      <div className="listing-details__author ld-chunk ld-chunk-2">
        <Link
          to={`/profile/${author.id}`}
          className="author-link"
          aria-label="Профіль автора"
          onClick={openAuthorProfile}
        >
          <img
            src={author.avatar || DEFAULT_AVATAR}
            alt={author.username || "Користувач"}
            className="author-avatar"
            loading="lazy"
          />
          <div className="author-meta">
            <span className="author-username">{author.username || "Користувач"}</span>
            {createdHuman && <span className="author-time">{createdHuman}</span>}
          </div>
        </Link>

        {!isOwn && (
          <button
            className={`follow-btn ${isFollowing ? "following" : ""}`}
            onClick={toggleFollow}
            aria-label={isFollowing ? "Відписатися" : "Підписатися"}
          >
            {isFollowing ? "Відстежується" : "Стежити"}
          </button>
        )}
      </div>

<div
  className="listing-details__statsRow listing-details__statsRow--actionsOnly ld-chunk ld-chunk-3"
  aria-label="Дії оголошення"
>
  <div
    className="listing-details__actionsPill listing-details__actionsPill--noCounts"
    aria-label="Дії оголошення"
  >
    <ActionBar
      liked={isLiked}
      saved={isSaved}
      likeCount={likesCount}
      commentCount={commentCount}
      shareCount={shareCount}
      savedCount={savesCount}
      onLike={toggleLike}
      onComment={openComments}
      onShare={openShare}
      onSave={openSaveSheet}
      variant="inline"
      context="listingDetails"
    />
  </div>
</div>


      {/* Контент */}
      <div className="listing-details__content ld-chunk ld-chunk-4">
        <h1 className="listing-details__title">{listing?.title || "Оголошення"}</h1>

        {isSaleDeal && Number(listing?.price) > 0 && (
          <p className="listing-details__price">
            {Number(listing.price).toLocaleString("uk-UA")} {listing.currency || "₴"}
          </p>
        )}

        {isExchangeDeal && (
          <>
            <p className="listing-details__price">Обмін</p>
            {exchangeForText && (
              <p className="listing-details__exchangeFor">на {exchangeForText}</p>
            )}
          </>
        )}

        {isFreeDeal && (
          <p className="listing-details__price">Безкоштовно</p>
        )}

        {/* БЕЙДЖІ: СТАН / ТОРГ / ОБМІН */}
        {(conditionLabel || showNegotiable || showExchange) ? (
          <div className="listing-details__badges">
            {conditionLabel ? (
              <div className="listing-details__badge">{`Стан: ${conditionLabel}`}</div>
            ) : null}
            {showNegotiable ? (
              <div className="listing-details__badge">Можливий торг</div>
            ) : null}
            {showExchange ? (
              <div className="listing-details__badge">Можливий обмін</div>
            ) : null}
          </div>
        ) : null}

        {listing?.description && <p className="listing-details__lead">{listing.description}</p>}

        {/* Атрибути */}
        {(() => {
          const { labelMap, orderedKeys } = (() => {
            const cat = getPureCategory(listing?.category);
            const def = cat && categoryFieldsConfig[cat] ? categoryFieldsConfig[cat] : null;
            const map = {};
            const order = [];
            if (!def) return { labelMap: map, orderedKeys: order };
            const attrs = listing?.attributes || {};
            const fields = typeof def === "function" ? def(attrs) : def;
            if (Array.isArray(fields)) {
              for (const f of fields) {
                const visible = typeof f.showIf === "function" ? !!f.showIf(attrs) : true;
                if (f?.name && f?.label) map[f.name] = f.label;
                if (visible && f?.name) order.push(f.name);
              }
            }
            return { labelMap: map, orderedKeys: order };
          })();

          const attrs = listing?.attributes || {};
          const keys = Object.keys(attrs);
          if (keys.length === 0) return null;

          const known = orderedKeys.filter((k) => k in attrs);
          const unknown = keys.filter((k) => !orderedKeys.includes(k));

          const rows = [];
          known.forEach((key) =>
            rows.push({ key, label: labelMap[key] || prettifyKey(key), value: attrs[key] })
          );
          unknown.forEach((key) =>
            rows.push({ key, label: labelMap[key] || prettifyKey(key), value: attrs[key] })
          );

          return (
            <div className="listing-details__attributes">
              <h3 className="listing-details__attributes-title">Додаткова інформація</h3>
              {rows.map(({ key, label, value }) => (
                <div key={key} className="attribute-row">
                  <span className="attribute-key">{label}:</span>
                  <span className="attribute-value">{formatAttrValue(value)}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ІНШІ ОГОЛОШЕННЯ АВТОРА */}
      <AuthorListingsRow
        listingId={listing?.listingId}
        authorId={listing?.userId}
        authorUsername={author?.username}
      />

      {/* СЕКЦІЯ «СХОЖІ ОГОЛОШЕННЯ» */}
      <SimilarListings listingId={listing?.listingId} category={listing?.category} />

      <div className="listing-details__ad">
        <AdSlot placement="listing_details_bottom" />
      </div>

      {!isOwn && <div className="listing-details__bottom-spacer" aria-hidden="true" />}

      {!isOwn && (
        <div className="listing-details__bottom ld-chunk ld-chunk-5" onClick={(e) => e.stopPropagation()}>
          <div className="bottom-input">
            <input
              ref={inputRef}
              type="text"
              placeholder="Написати автору…"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") sendMessage();
              }}
              aria-label="Написати автору"
              style={{ fontSize: "16px" }}
            />

            <button className="send-btn" aria-label="Надіслати" onClick={sendMessage}>
              <img src={sendIcon} alt="" aria-hidden="true" />
            </button>

            {phoneHref && (
              <a className="bottom-call-btn" href={phoneHref} aria-label="Подзвонити" title={phoneDisplay || "Подзвонити"}>
                <img src={callIcon} alt="" aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
      )}

      <CommentsModal
        open={commentsOpen}
        listingId={listing?.listingId}
        listingAuthorId={listing?.userId}
        onClose={() => setCommentsOpen(false)}
        onCountChange={(delta) => setCommentCount((c) => Math.max(0, c + Number(delta || 0)))}
      />

      {shareOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <ShareModal
            open={shareOpen}
            onClose={() => {
              setShareOpen(false);
              fetchShareCount();
            }}
            shareUrl={shareUrl}
            shareText={shareText}
            onShared={registerShare}
          />,
          document.body
        )}

      {saveSheetOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <SaveSheetModal
            open={saveSheetOpen}
            onClose={() => setSaveSheetOpen(false)}
            userId={getCurrentUserId()}
            postId={listing?.listingId}
            type="listing"
            thumbnail={saveSheetThumb}
            title="Збереження"
            onChanged={({ saved }) => {
              const was = !!(typeof isSaved === "boolean" ? isSaved : false);
              const willBe = !!saved;

              const delta =
                willBe && !was ? 1 :
                !willBe && was ? -1 :
                0;

              if (willBe && !was) {
                trackSaveListing({
                  listingId: listing?.listingId,
                  category: listing?.category || null,
                });
              }

              // 1) локальний UI ListingDetails
              setIsSaved(willBe);

              setSavesCount((c) => {
                const cur = Number.isFinite(c) ? c : 0;
                if (willBe && !was) return cur + 1;
                if (!willBe && was) return Math.max(0, cur - 1);
                return cur;
              });

              setListing((prev) => {
                const base = Number(prev?.favoritesCount || 0);
                if (willBe && !was) return { ...(prev || {}), favoritesCount: base + 1 };
                if (!willBe && was) return { ...(prev || {}), favoritesCount: Math.max(0, base - 1) };
                return prev || {};
              });

              // 2) глобальна подія — щоб ListingCard миттєво підхопив
              try {
                const id = listing?.listingId;
                if (id) {
                  const curAbs = Number(savesCount || 0);
                  const nextAbs = Number.isFinite(curAbs) ? Math.max(0, curAbs + delta) : undefined;

                  window.dispatchEvent(
                    new CustomEvent("cv-saved-updated", {
                      detail: {
                        listingId: id,
                        saved: willBe,
                        delta,
                        absolute: Number.isFinite(nextAbs) ? nextAbs : undefined,
                      },
                    })
                  );
                }
              } catch {}
            }}
          />,
          document.body
        )}

      <PhotoDetails
        open={photoOpen}
        photos={photos.map((src) => ({ src }))}
        startIndex={photoIndex}
        onClose={() => setPhotoOpen(false)}
      />

      {actionsOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <ListingDetailsActions
          isOwn={isOwn}
          isLeaving={leaving}
          onClose={closeActions}
          onArchive={handleArchive}
          onDelete={handleDelete}
          onEdit={handleEdit}
          listingType={listing?.type}
          listingId={listing?.listingId}
          onRaise={handleRaise}
          onNotInteresting={handleNotInteresting}
          onWriteAuthor={handleWriteAuthor}
          raisedUntil={listing?.raisedUntil || null}
          lastRaisedAt={listing?.raisedAt || null}
        />,
          document.body
        )}

      <BoostSheet
        open={boostSheetOpen}
        onClose={() => setBoostSheetOpen(false)}
        listingId={listing?.listingId}
        onBoosted={(data) => {
          setListing((prev) =>
            prev ? { ...prev, raisedUntil: data?.raisedUntil || prev.raisedUntil } : prev
          );
          setBoostSheetOpen(false);
        }}
      />

        <Alerts
          open={confirmArchiveOpen}
          variant="deleteListing"
          title="Архівувати оголошення?"
          text="Воно переміститься у ваш архів."
          primaryText="Архівувати"
          secondaryText={null}
          cancelText="Скасувати"
          tone="default"
          onCancel={() => setConfirmArchiveOpen(false)}
          onClose={() => setConfirmArchiveOpen(false)}
          onPrimary={runConfirmedArchive}
        />

        <Alerts
          open={confirmDeleteOpen}
          variant="deleteListing"
          title="Видалити оголошення?"
          text="Цю дію неможливо відмінити."
          primaryText="Видалити"
          secondaryText={null}
          cancelText="Скасувати"
          tone="danger"
          onCancel={() => setConfirmDeleteOpen(false)}
          onClose={() => setConfirmDeleteOpen(false)}
          onPrimary={runConfirmedDelete}
        />
    </div>
  );
};

export default ListingDetails;
